import { defineMessages } from 'react-intl';

export const scope = 'app.containers.ApprovalRules';

/**
 * recruiter-ui uses the legacy FormatJS extraction: manual dot-notation ids, and
 * `description` as the in-code record of what a string means. Both are required
 * here — an omitted id silently never reaches the translation file.
 *
 * Terminology is "rule", not "flow" (AXHE-4015, renamed per Michaela). The design
 * handoff predates that rename and still says "flow" throughout; the copy here
 * deliberately does not.
 */
export default defineMessages({
  // The surface is "Approvals" (AXHE-4472); "Approval rules" is now the name of
  // what one tab holds, not of the page. A separate id rather than a reworded
  // `pageTitle`, because the two say different things and a translated catalog
  // carries the old sentence forward under an unchanged id.
  pageHeading: {
    id: `${scope}.pageHeading`,
    defaultMessage: 'Approvals',
    description:
      'Title of the page where approvals are configured and reviewed; also the browser tab title',
  },
  pageSubtitle: {
    id: `${scope}.pageSubtitle`,
    defaultMessage:
      'Opening approvals and offer approvals allow more control over when an opening can become active or an offer can be extended by requiring it to be reviewed and approved by an assigned approver group.',
    description:
      'Explains, under the page title, what approvals do and why an admin would configure them',
  },
  backToSettings: {
    id: `${scope}.backToSettings`,
    defaultMessage: 'Back to settings',
    description:
      'Link above the page title, returning to the account settings area this page is reached from',
  },
  pageTitle: {
    id: `${scope}.pageTitle`,
    defaultMessage: 'Approval rules',
    description:
      'Names the rules that decide when an offer or opening needs sign-off; the accessible name of the list while it loads',
  },
  approverRulesTab: {
    id: `${scope}.approverRulesTab`,
    defaultMessage: 'Approver rules',
    description:
      'Tab holding the rules that decide when an offer or opening needs sign-off',
  },
  // Names the segmented control the two entries below sit in. A group of toggle
  // buttons has no visible heading, so this is the only thing telling a screen
  // reader what the pair chooses between. "Type" is the word the table's own
  // column uses for the same distinction.
  subjectTypeLabel: {
    id: `${scope}.subjectTypeLabel`,
    defaultMessage: 'Rule type',
    description:
      'Accessible name of the segmented control choosing whether the table lists offer rules or opening rules',
  },
  // The two ids say "tab" and the control is now a segmented toggle. Kept as
  // they are: the words themselves have not changed, and reminting an id
  // orphans the sentence in every translated catalog that already carries it.
  offersTab: {
    id: `${scope}.offersTab`,
    defaultMessage: 'Offers',
    description: 'Filter showing only rules that gate offers to candidates',
  },
  openingsTab: {
    id: `${scope}.openingsTab`,
    defaultMessage: 'Openings',
    description: 'Filter showing only rules that gate job openings',
  },
  searchLabel: {
    id: `${scope}.searchLabel`,
    defaultMessage: 'Search rules',
    description: 'Label for the input that filters the rule list by name',
  },
  // Names the whole thing being created, because this button is the only entry
  // point to authoring one and "rule" alone does not say what kind. Sentence
  // case: the mock title-cases it, but every other button in the product is
  // sentence case and the design system says so explicitly.
  createRule: {
    id: `${scope}.createRule`,
    defaultMessage: 'Create approval rule',
    description:
      'Split button, with a caret, that opens the menu for starting a new approval rule',
  },
  levelCount: {
    id: `${scope}.levelCount`,
    defaultMessage:
      '{count, plural, one {# approval level} other {# approval levels}}',
    description:
      'How many sequential rounds of sign-off a rule requires before it is satisfied',
  },

  // --- table columns (AXHE-4464) ---
  // Design Requirements §2 heads the first column "Rules". Singular here: every
  // other header in the row names one cell's contents ("Type", "Trigger",
  // "Enabled"), and "Approvers" is plural only because a cell holds several.
  // A header is a label for the value under it, and the value is one rule.
  // The list's own loading state. Distinct from `pageTitle`, which now names the
  // scroll region and the table: three elements answering to "Approval rules"
  // would have a screen reader say it three times on the way into one table.
  loadingRules: {
    id: `${scope}.loadingRules`,
    defaultMessage: 'Loading approval rules',
    description:
      'Accessible name of the spinner shown in the table body while the rule list is being fetched',
  },
  columnRule: {
    id: `${scope}.columnRule`,
    defaultMessage: 'Rule',
    description: 'Column header over the name of each approval rule',
  },
  columnType: {
    id: `${scope}.columnType`,
    defaultMessage: 'Type',
    description:
      'Column header over the badge saying whether a rule gates offers or openings',
  },
  columnTrigger: {
    id: `${scope}.columnTrigger`,
    defaultMessage: 'Trigger',
    description:
      'Column header over the summary of what makes a rule or one of its levels apply',
  },
  columnApprovers: {
    id: `${scope}.columnApprovers`,
    defaultMessage: 'Approvers',
    description: 'Column header over the people who can sign off',
  },
  columnEnabled: {
    id: `${scope}.columnEnabled`,
    defaultMessage: 'Enabled',
    description:
      'Column header over the switch turning a rule on and off. Names the state, not the act — the cell holds a switch, and the header labels what it reports',
  },
  // The actions column shows two icon buttons and no visible header. The header
  // still exists for a screen reader reading the table by column, which is the
  // one context where an unnamed column is a gap rather than a clean design.
  columnActions: {
    id: `${scope}.columnActions`,
    defaultMessage: 'Actions',
    description:
      'Accessible-only column header over the edit and delete buttons on each rule row',
  },
  typeOffer: {
    id: `${scope}.typeOffer`,
    defaultMessage: 'Offer',
    description:
      'Badge in the Type column marking a rule that gates offers sent to candidates',
  },
  typeOpening: {
    id: `${scope}.typeOpening`,
    defaultMessage: 'Opening',
    description:
      'Badge in the Type column marking a rule that gates job openings',
  },
  editRule: {
    id: `${scope}.editRule`,
    defaultMessage: 'Edit rule',
    description:
      'Icon button on a rule row that opens the panel for changing that rule',
  },
  // Wraps the row's visible label rather than replacing it, so the accessible
  // name is a superset of what a speech-input user can read — WCAG 2.5.3 (Label
  // in Name). "Edit level {number}" was a replacement: the row reads
  // "Level 2 · Finance sign-off" and answered to "Edit level 2", so saying what
  // you see matched nothing.
  //
  // Still names the level rather than the rule, which is the reason this message
  // exists at all: four rows each open the panel on a different card, and one
  // "Edit rule" on all of them makes that difference invisible.
  editLevel: {
    id: `${scope}.editLevel`,
    defaultMessage: 'Edit {level}',
    description:
      'Accessible name of the control on a level row, which opens the rule panel at that level. {level} is the row\'s own visible label, so the name contains it rather than replacing it',
  },
  // Level rows are numbered by position, and a name is optional on a level —
  // most rules leave it blank, so the number has to carry the row on its own.
  levelRow: {
    id: `${scope}.levelRow`,
    defaultMessage: 'Level {number}',
    description: 'Label of a nested level row, when the level has no name',
  },
  levelRowNamed: {
    id: `${scope}.levelRowNamed`,
    defaultMessage: 'Level {number} · {name}',
    description:
      'Label of a nested level row, pairing its position with the name the admin gave it',
  },

  // --- trigger summary (AXHE-4464 §2) ---
  // What makes a rule, or one of its levels, apply. Read out of the stored
  // condition rather than fetched: `Approvals::Condition` is the authority on
  // what a condition means, and the operator words are already translated above
  // for the condition builder. Sentence fragments, not sentences — they sit in a
  // table cell, so no terminal punctuation.
  triggerAllOffers: {
    id: `${scope}.triggerAllOffers`,
    defaultMessage: 'All offers',
    description:
      'Trigger summary for a rule with no conditions, which therefore gates every offer',
  },
  triggerAllOpenings: {
    id: `${scope}.triggerAllOpenings`,
    defaultMessage: 'All openings',
    description:
      'Trigger summary for a rule with no conditions, which therefore gates every opening',
  },
  triggerAlways: {
    id: `${scope}.triggerAlways`,
    defaultMessage: 'Always',
    description:
      'Trigger summary for a level with no conditions, which therefore applies whenever the rule matched',
  },
  triggerPosition: {
    id: `${scope}.triggerPosition`,
    defaultMessage: 'Position: {list}',
    description:
      'Trigger summary naming the positions a rule or level is scoped to',
  },
  triggerLocation: {
    id: `${scope}.triggerLocation`,
    defaultMessage: 'Location: {list}',
    description:
      'Trigger summary naming the locations a rule or level is scoped to',
  },
  // Location groups are stored by id rather than name, so they can be counted
  // here but not named. Used when a condition selects groups and no individual
  // locations, where there is no name to lead with.
  triggerLocationGroups: {
    id: `${scope}.triggerLocationGroups`,
    defaultMessage:
      'Location: {count, plural, one {# location group} other {# location groups}}',
    description:
      'Trigger summary for a scope made up of location groups, which are stored by identifier and so cannot be named here',
  },
  triggerOpenings: {
    id: `${scope}.triggerOpenings`,
    defaultMessage:
      '{count, plural, one {# selected opening} other {# selected openings}}',
    description:
      'Trigger summary for a scope naming specific openings, which are stored by identifier and so cannot be named here',
  },
  triggerMore: {
    id: `${scope}.triggerMore`,
    defaultMessage: '{names} +{count}',
    description:
      'Shortens a long list in the trigger summary to the first few names plus how many are left',
  },
  triggerFieldValue: {
    id: `${scope}.triggerFieldValue`,
    defaultMessage: '{field} {operator} {value}',
    description:
      'Trigger summary for a condition written about a field, read left to right as a sentence — for example "Sign-on bonus greater than 10000"',
  },
  triggerFieldBare: {
    id: `${scope}.triggerFieldBare`,
    defaultMessage: '{field} {operator}',
    description:
      'Trigger summary for a condition whose comparison takes no value, such as "Cost Code is present"',
  },
  triggerPayOutsideRange: {
    id: `${scope}.triggerPayOutsideRange`,
    defaultMessage: 'Compensation: outside pay range',
    description:
      "Trigger summary for a pay condition firing when the offer is below or above the opening's posted range",
  },
  triggerPayAboveRange: {
    id: `${scope}.triggerPayAboveRange`,
    defaultMessage: 'Compensation: above pay range',
    description:
      "Trigger summary for a pay condition firing when the offer is above the top of the opening's posted range",
  },
  triggerPayBelowRange: {
    id: `${scope}.triggerPayBelowRange`,
    defaultMessage: 'Compensation: below pay range',
    description:
      "Trigger summary for a pay condition firing when the offer is below the bottom of the opening's posted range",
  },
  triggerPayOver: {
    id: `${scope}.triggerPayOver`,
    defaultMessage: 'Compensation: over {amount}',
    description:
      'Trigger summary for a pay condition firing above an amount the admin typed in',
  },
  triggerPayUnder: {
    id: `${scope}.triggerPayUnder`,
    defaultMessage: 'Compensation: under {amount}',
    description:
      'Trigger summary for a pay condition firing below an amount the admin typed in',
  },
  triggerPayOutsideAmounts: {
    id: `${scope}.triggerPayOutsideAmounts`,
    defaultMessage: 'Compensation: outside {min}–{max}',
    description:
      'Trigger summary for a pay condition firing outside a band the admin typed in',
  },
  // Every condition type the current editor writes has a phrasing above, so this
  // only appears for a rule authored against a build this one predates, or
  // through the API. Says how many rather than staying silent: an empty Trigger
  // cell on a live gate reads as "this rule catches nothing".
  triggerUnnamedConditions: {
    id: `${scope}.triggerUnnamedConditions`,
    defaultMessage:
      '{count, plural, one {# condition} other {# conditions}}',
    description:
      'Trigger summary fallback, counting conditions this page has no wording for rather than leaving the cell empty',
  },
  triggerAndMore: {
    id: `${scope}.triggerAndMore`,
    defaultMessage:
      '{summary} +{count, plural, one {# more condition} other {# more conditions}}',
    description:
      'Appended to a trigger summary that describes only some of the conditions, so the row admits it is showing less than the rule holds',
  },
  // Wraps one half of a single-level rule's combined trigger when that half is an
  // "or" of several conditions, so the "and" joining the rule's scope to its
  // level does not rebind the looser "or" — "(Position: Driver or Location:
  // Dallas) and Compensation: over 50,000" rather than a flat join that reads as
  // "Position or (Location and Compensation)".
  triggerGroup: {
    id: `${scope}.triggerGroup`,
    defaultMessage: '({group})',
    description:
      'Parenthesises a group of "or" conditions inside a combined trigger summary so the surrounding "and" cannot be misread as binding tighter',
  },
  approverCount: {
    id: `${scope}.approverCount`,
    defaultMessage: '{count, plural, one {# approver} other {# approvers}}',
    description: 'How many people can sign off at a single round of approval',
  },
  disabledRule: {
    id: `${scope}.disabledRule`,
    defaultMessage: 'Off',
    description:
      'Badge on a rule that is configured but currently not gating anything',
  },
  disabledLevel: {
    id: `${scope}.disabledLevel`,
    defaultMessage: 'Off',
    description:
      'Badge on a level the rule skips. Separate from the rule badge with the same wording because the two label different objects and a translation may need to agree with a different noun',
  },
  managedElsewhere: {
    id: `${scope}.managedElsewhere`,
    defaultMessage: 'Managed on Opening Approvals',
    description:
      'Badge on a rule that was carried over from the older approvals page and can only be edited there',
  },
  managedElsewhereHint: {
    id: `${scope}.managedElsewhereHint`,
    defaultMessage:
      'This rule came from the Opening Approvals page. Edit it there — changes made here would be overwritten.',
    description:
      'Explains why a carried-over rule cannot be edited on this page and where to edit it instead',
  },
  // An account with no rules and a search that matched nothing are two states,
  // not one (AXHE-4468). Design Requirements §2 gives a single "No approval
  // rules found." covering both; `layout-composition` is explicit that they do
  // not collapse — "Empty state (no data) and search/filter zero-result state
  // are separate cases — provide distinct messaging for each" — and the two
  // genuinely have different ways out, one recoverable by clearing the search
  // and the other needing a rule created. Keep both. The doc's wording is also
  // a title with a full stop, which `copywriting-voice` does not allow.
  emptyTitle: {
    id: `${scope}.emptyTitle`,
    defaultMessage: 'No approval rules yet',
    description:
      'Heading shown when an account has not configured any approval rules',
  },
  emptyBody: {
    id: `${scope}.emptyBody`,
    defaultMessage:
      'Create a rule to require sign-off before an offer or opening goes out.',
    description:
      'Explains what creating an approval rule accomplishes, shown when none exist',
  },
  noSearchResultsTitle: {
    id: `${scope}.noSearchResultsTitle`,
    defaultMessage: 'No results for "{term}"',
    description:
      'Heading shown when a rule name search matched nothing, naming what was searched',
  },
  noSearchResultsBody: {
    id: `${scope}.noSearchResultsBody`,
    defaultMessage: 'Check the spelling or clear the search to see every rule.',
    description: 'Tells the user how to recover from a search that found nothing',
  },
  clearSearch: {
    id: `${scope}.clearSearch`,
    defaultMessage: 'Clear search',
    description:
      'Button in the no-results state that empties the search field, bringing the whole rule list back',
  },
  // The search box's own clear button, which `SearchInput` otherwise names from
  // `globalMessages.clearSearch` — also "Clear search", and also on screen
  // whenever the field has text. Two controls with one accessible name doing one
  // job is ambiguous for anyone navigating by name, and it made
  // `getByRole('button', { name: 'Clear search' })` match two elements. Named
  // for the thing being searched, matching `searchLabel` above; the empty
  // state's wording is visible copy and stays as it is (#4497).
  clearSearchField: {
    id: `${scope}.clearSearchField`,
    defaultMessage: 'Clear rule search',
    description:
      'Accessible name of the button inside the search box that empties it, distinct from the no-results state button that does the same job',
  },
  loadError: {
    id: `${scope}.loadError`,
    defaultMessage: "We couldn't load your approval rules. Try again.",
    description:
      'Shown when the request for the rule list failed, with the recovery action',
  },
  retry: {
    id: `${scope}.retry`,
    defaultMessage: 'Try again',
    description: 'Button that re-requests the rule list after a failure',
  },

  // --- editor panel ---
  // The two items name the entity, not the rule: the button above them already
  // said "approval rule", so repeating it here would read "Create approval rule
  // › Offer rule". What the menu is actually asking is which entity the rule
  // gates, and a rule's subject type is fixed at creation — so this choice is
  // the one thing the menu exists to make.
  createOfferRule: {
    id: `${scope}.createOfferRule`,
    defaultMessage: 'Offer',
    description:
      'Menu choice that starts a rule gating offers sent to candidates',
  },
  createOpeningRule: {
    id: `${scope}.createOpeningRule`,
    defaultMessage: 'Opening',
    description: 'Menu choice that starts a rule gating job openings',
  },
  // `subject` selects the noun on all four of these rather than each one being a
  // pair of ids: one rule table backs both filters, a rule's subject type is
  // fixed at creation, and the panel always knows which it has. The slot holds a
  // bare noun and the frame around it is fixed, so a translation is free to put
  // the slot wherever its own word order needs it. Same form `deleteRulePending`
  // uses, for the same reason.
  //
  // Sentence case, and the mock title-cases these (Design Requirements §3 has
  // "Create Offer Approval Rule"). AXHE-4466 already departed from the mock on
  // exactly this for the split button above — see `createRule` — so following the
  // mock here would put "Create Offer Approval Rule" in the footer of a panel
  // opened by a "Create approval rule" button.
  createRuleTitle: {
    id: `${scope}.createRuleTitle`,
    defaultMessage:
      'Create {subject, select, Funnel {opening} other {offer}} approval rule',
    description:
      'Heading of the panel where a new rule is written. {subject} is the rule\'s subject type, which decides whether the heading names offers or openings',
  },
  editRuleTitle: {
    id: `${scope}.editRuleTitle`,
    defaultMessage:
      'Edit {subject, select, Funnel {opening} other {offer}} approval rule',
    description:
      'Heading of the panel where an existing rule is changed. {subject} is the rule\'s subject type, which decides whether the heading names offers or openings',
  },
  // Edit only.
  //
  // "scope" is added to the mock's wording, which enumerates only "levels and
  // approvers". That was accurate for the panel the mock was drawn against; this
  // ticket makes the rule's scope editable here too, so the mock's list is now
  // short by the one thing that decides which offers the rule touches at all.
  editRuleIntro: {
    id: `${scope}.editRuleIntro`,
    defaultMessage:
      "Edit this {subject, select, Funnel {opening} other {offer}} approval rule's scope, levels and approvers.",
    description:
      'Sentence under the edit heading, saying what the panel changes. {subject} is the rule\'s subject type, which decides whether it names an offer or an opening rule',
  },
  // Create only, and the counterpart to `editRuleIntro`: the two share a slot,
  // because an empty panel needs the model explained where an existing rule
  // needs the edit's reach explained. Design Requirements §3 asks for it —
  // "intro copy explains that every offer/opening passes through each level in
  // order, and a level is skipped if its condition doesn't apply".
  //
  // "this rule applies to" is the one addition to the wording §3 gives, and it
  // is what makes the sentence survive the panel. This is an intro, so it is on
  // screen for the whole of create — including after the admin narrows the scope
  // to "Offers that meet conditions". Quantified over offers outright, as both
  // §3 and `scopeAllLevels` are, it would then claim the rule reaches offers it
  // no longer touches. Naming the rule's own reach is true on either branch, and
  // borrows the verb from the "Apply this rule to" choice it is describing.
  //
  // Deliberately says nothing about how many levels exist: it describes what the
  // panel builds, not the draft in front of the admin. `scopeAllLevels` is the
  // sentence that describes the draft, and it stays gated on both scope and
  // level count. The two say one idea, so the panel drops that one while this
  // is up.
  createRuleIntro: {
    id: `${scope}.createRuleIntro`,
    defaultMessage:
      'Every {subject, select, Funnel {opening} other {offer}} this rule applies to passes through each level in order. A level is skipped when its conditions do not apply.',
    description:
      "Sentence under the create heading, explaining how a rule's levels run. {subject} is the rule's subject type, which decides whether it names offers or openings",
  },
  ruleNameLabel: {
    id: `${scope}.ruleNameLabel`,
    defaultMessage: 'Rule name',
    description: 'Label for the field naming an approval rule',
  },
  // A prompt, not the label — the label sits above the box and stays there once
  // the field holds a value.
  ruleNamePlaceholder: {
    id: `${scope}.ruleNamePlaceholder`,
    defaultMessage: 'Enter rule name',
    description:
      'Placeholder shown in the empty field where an approval rule is named',
  },
  // Design Requirements §3 words this "Rule name is required" (AXHE-4468). It
  // stays phrased as the action instead: `constraint-anti-pattern-guard`
  // prohibits an error that only describes what went wrong, and
  // `copywriting-voice`'s error table gives this exact pair — "Required field"
  // ✗ / "Enter a job title" ✓. §3 asks for two things, and the other one is
  // now built: the field is marked required on its label, which is where "is
  // required" is said. This message is the part that says what to do about it.
  ruleNameRequired: {
    id: `${scope}.ruleNameRequired`,
    defaultMessage: 'Enter a rule name',
    description:
      'Validation message when the rule name field was left empty, phrased as the action to take',
  },
  // --- rule scope (AXHE-4467) ---
  // A noun phrase, not a question: "Apply this rule to" names the choice the
  // radios make. The two options are the only two states a rule's scope has —
  // conditions that always match, or conditions that select.
  scopeLegend: {
    id: `${scope}.scopeLegend`,
    defaultMessage: 'Apply this rule to',
    description:
      'Label for the choice between gating every offer or opening and gating only the ones matching conditions',
  },
  scopeAll: {
    id: `${scope}.scopeAll`,
    defaultMessage: 'All {subject, select, Funnel {openings} other {offers}}',
    description:
      'Choice that applies the rule to every offer or opening, with no conditions. {subject} is the rule\'s subject type',
  },
  scopeConditional: {
    id: `${scope}.scopeConditional`,
    defaultMessage:
      '{subject, select, Funnel {Openings} other {Offers}} that meet conditions',
    description:
      'Choice that applies the rule only to the offers or openings its conditions select. {subject} is the rule\'s subject type',
  },
  // Only with more than one level: a one-level rule has no "next level" to
  // describe, so the sentence would explain a sequence that does not exist. Also
  // only on edit — on create `createRuleIntro` is already saying this at the top
  // of the panel, and the two would sit a few lines apart.
  scopeAllLevels: {
    id: `${scope}.scopeAllLevels`,
    defaultMessage:
      "All {subject, select, Funnel {openings} other {offers}} pass through every level. Approval is required before moving to the next level, unless a level's own conditions exclude it.",
    description:
      'Explains what applying a multi-level rule to everything means: each level is asked in turn, and only a level whose own condition fails is skipped. {subject} is the rule\'s subject type',
  },
  // Phrased as the two ways out rather than as what is missing, which is what
  // `copywriting-voice` asks of an error — and here there genuinely are two, so
  // naming only the first would hide the one an admin who picked the wrong radio
  // actually wants.
  scopeNeedsCondition: {
    id: `${scope}.scopeNeedsCondition`,
    defaultMessage:
      'Add a condition, or apply this rule to all {subject, select, Funnel {openings} other {offers}}',
    description:
      'Validation message when a rule is set to apply only to what meets conditions but has none. {subject} is the rule\'s subject type',
  },
  // Said out loud because the alternative is a radio pair that lies. A rule
  // scoped by a position, location, opening or attribute condition genuinely does
  // not apply to everything, and this panel renders none of those types
  // (AXHE-4383) — so it can neither show what the scope is nor honestly offer to
  // drop it.
  scopeConditionsElsewhere: {
    id: `${scope}.scopeConditionsElsewhere`,
    defaultMessage:
      'This rule is scoped by conditions of a kind this panel does not show. They are kept as they are, so the rule cannot be applied to all {subject, select, Funnel {openings} other {offers}} from here.',
    description:
      "Read-only notice on a rule whose scope uses condition types the panel cannot render, saying that saving keeps them and that this is why the all-offers choice is unavailable. {subject} is the rule's subject type",
  },
  // The no-catalog twin of `scopeConditionsElsewhere`. When the field catalog
  // fails to load, the rule's own data-key scope rows are hidden too — the
  // builder shows its error state instead of them — so "All …" is withheld for a
  // reason that is recoverable, not permanent. Blaming the *kind* of condition
  // here would be wrong: these are exactly the kind the panel renders, and the
  // builder below offers a Retry that would bring them back. Named for the cause
  // the admin can act on.
  scopeConditionsUnavailable: {
    id: `${scope}.scopeConditionsUnavailable`,
    defaultMessage:
      "This rule's conditions can't be loaded, so it can't be applied to all {subject, select, Funnel {openings} other {offers}} from here yet. They are kept as they are.",
    description:
      "Read-only notice on the rule scope when the field catalog failed to load, so the rule's own conditions can't be shown and the all-offers choice is withheld until they load. Distinct from scopeConditionsElsewhere, which is about condition types the panel never renders. {subject} is the rule's subject type",
  },

  // Shown when the stored scope holds a "matches everything" row beside a
  // narrowing one, and the panel is on the conditional choice. Saving drops the
  // "everything" row — which is right for a conditional rule — but under OR logic
  // that row is what makes the *stored* rule gate every subject, so the drop
  // changes what fires. Two strings, chosen by the rule's condition logic: under
  // AND the row is redundant and the drop is only tidying; under OR it narrows,
  // which is the one the admin has to be told before it happens silently.
  scopeAlwaysDropped: {
    id: `${scope}.scopeAlwaysDropped`,
    defaultMessage:
      'This rule also has a condition that matches every {subject, select, Funnel {opening} other {offer}}. Saving removes it and keeps only the conditions below.',
    description:
      "Notice that an existing 'matches everything' scope row will be removed on save because the rule is set to apply only to what its conditions select, under AND logic where the row is redundant. {subject} is the rule's subject type",
  },
  scopeAlwaysDroppedNarrows: {
    id: `${scope}.scopeAlwaysDroppedNarrows`,
    defaultMessage:
      'This rule currently applies to every {subject, select, Funnel {opening} other {offer}}. Saving removes that and applies it only to the {subject, select, Funnel {openings} other {offers}} the conditions below select.',
    description:
      "Warning that an existing 'matches everything' scope row will be removed on save, and because the rule combines its scope conditions with OR that row is what makes it apply to everything today, so saving narrows what the rule gates. {subject} is the rule's subject type",
  },
  levelHeading: {
    id: `${scope}.levelHeading`,
    defaultMessage: 'Level {number}',
    description:
      'Heading of one round of sign-off within a rule, numbered by its order',
  },
  levelNameLabel: {
    id: `${scope}.levelNameLabel`,
    defaultMessage: 'Level name',
    description: 'Label for the field naming one round of sign-off',
  },
  // Placeholder for that field once it sits inline in the card's header band,
  // where the visible label would crowd the row. It says what the label cannot —
  // that the name is optional — and never stands in for the label itself, which
  // the field carries as an `aria-label`.
  //
  // "name", not the mock's "label": the panel already calls this a level name,
  // and a second word for one idea reads as a second thing.
  levelNamePlaceholder: {
    id: `${scope}.levelNamePlaceholder`,
    defaultMessage: 'Optional name',
    description:
      'Placeholder in the field naming one round of sign-off, saying that naming it is optional',
  },
  levelOff: {
    id: `${scope}.levelOff`,
    defaultMessage:
      'This level is off, so the rule skips it. Saving keeps it off — levels are no longer switched on and off here.',
    description:
      'Read-only notice on a level that was switched off before per-level toggles were removed, saying that the rule ignores it and that saving does not change that',
  },
  approversLabel: {
    id: `${scope}.approversLabel`,
    defaultMessage: 'Approvers',
    description: 'Label for the people who can sign off at this round',
  },
  // Design Requirements §3 words this "Each level requires at least one
  // approver." (AXHE-4468). "needs" and "requires" say the same thing, so the
  // only real difference is the full stop, and `copywriting-voice`'s rule is
  // one sentence takes none, two or more take punctuation throughout — which
  // is exactly how this file already splits: the single-sentence ones below
  // carry no stop, `conditionFieldUnavailable` and `conditionOperatorUnavailable`
  // are two sentences and do. This is one sentence. Not worth orphaning five
  // translated catalogs to add a period the guide asks us not to have.
  approverRequired: {
    id: `${scope}.approverRequired`,
    defaultMessage: 'Each level needs at least one approver',
    description:
      'Validation message when a round of sign-off was left with nobody able to approve it',
  },
  // --- how a level's approvals combine (AXHE-3899) ---
  // Design Requirements §3 draws this control with bare Any/All segments beside
  // the section's own "Approvers" heading, and the panel draws it that way. The
  // segments keep those words; the group gets a name of its own because a radio
  // group has no control to hang one on, and "Any" read out with nothing around
  // it says nothing. The name is the group's `aria-label` and is never shown.
  //
  // What this copy deliberately does not do is explain what "All" means once a
  // level has *escalated*. That is open on AXHE-4010, and `main` carries what
  // AXHE-4021 calls "a holding position, not an answer" — the readings on the
  // table differ only there, so this ships the control and not a sentence about
  // it.
  approverLogicLabel: {
    id: `${scope}.approverLogicLabel`,
    defaultMessage: 'Approvals needed',
    description:
      'Screen-reader name for the choice between one approval settling a round of sign-off and every approver having to give one',
  },
  approverLogicAny: {
    id: `${scope}.approverLogicAny`,
    defaultMessage: 'Any',
    description:
      'Choice meaning the first approval from any approver on the level settles it',
  },
  approverLogicAll: {
    id: `${scope}.approverLogicAll`,
    defaultMessage: 'All',
    description:
      'Choice meaning every approver on the level has to approve before the rule moves on',
  },
  offboardedApprovers: {
    id: `${scope}.offboardedApprovers`,
    defaultMessage:
      '{count, plural, one {# approver is no longer an active user} other {# approvers are no longer active users}} and will be removed from this level when you save',
    description:
      'Warning shown when a level lists approvers whose user accounts have been deactivated, so an admin knows that saving drops them',
  },
  // ## The backup-approver block (AXHE-3932, Design Requirements §3)
  //
  // §3 draws the block with an uppercase "BACKUP APPROVERS" caption above the
  // roster, and that caption is deliberately not built. It would carry the same
  // words as the roster's own label, one line above it and in the same position,
  // so it is a restatement rather than a section name — which is the case
  // `content-hierarchy` covers with "do not add a level of hierarchy just
  // because space exists for it".
  //
  // Not the same skill's "do not use headers to label form fields": that rule is
  // about a header standing *in place of* a label, and a section header over a
  // labelled field is normal ("Personal information" above "First name"). What
  // rules the caption out here is only that it duplicates the label, so if the
  // roster ever gains a second control the caption becomes worth having.
  //
  // The block still reads as a block: it sits below the primary roster, behind
  // its own rule (`styles.backupSection`), with its own remove control.
  backupApproversLabel: {
    id: `${scope}.backupApproversLabel`,
    defaultMessage: 'Backup approvers',
    description:
      'Label for the people asked to sign off at this round once the primary approvers have not responded in time',
  },
  addBackupApprovers: {
    id: `${scope}.addBackupApprovers`,
    defaultMessage: 'Add backup approvers',
    description:
      'Button that adds a stand-in roster and a wait time to one round of sign-off',
  },
  removeBackupApprovers: {
    id: `${scope}.removeBackupApprovers`,
    defaultMessage: 'Remove backup approvers',
    description:
      'Button that deletes the stand-in roster and its wait time from one round of sign-off',
  },
  // §3's sentence, "If no response after [N] [Hours | Days]", as one string with
  // the two controls interpolated into it.
  //
  // Not three pieces concatenated in source order. English puts the number and
  // the unit after the phrase; other languages do not, and a translator who can
  // only reword a leading fragment cannot move the controls relative to it.
  // react-intl is 5.x here, so an element value is a first-class placeholder and
  // the whole line is one translatable unit — the same reason the condition rows
  // never splice literal words between their pickers.
  escalateAfterSentence: {
    id: `${scope}.escalateAfterSentence`,
    defaultMessage: 'If no response after {amount} {unit}',
    description:
      'The wait before the backup approvers are asked. {amount} is a number field and {unit} an hours/days picker, both rendered inline — position them as the language requires',
  },
  // The names the two controls carry for assistive tech. Each stands on its own
  // rather than leaning on the sentence around it: a name that only makes sense
  // read together with neighbouring text is a name a screen-reader user never
  // hears whole. `aria-label` and not a placeholder — a real accessible name,
  // which is what `constraint-anti-pattern-guard` rules a placeholder out in
  // favour of.
  //
  // A `<fieldset>` with a `legend` visually hidden through `styles.visuallyHidden`
  // would add "if no response after" as group framing on top of these, and it is
  // available — the rules table already uses that class to name its actions
  // column — so "a legend would break the sentence" is not the reason. It is left out
  // because the sentence now *contains* both controls: a legend would either
  // repeat the whole interpolated line or invent a third name for the group, and
  // these two already stand on their own. Worth revisiting if the block grows a
  // third control.
  escalateAfterLabel: {
    id: `${scope}.escalateAfterLabel`,
    defaultMessage: 'Wait time',
    description:
      'Accessible label for how long to wait before the backup approvers are asked',
  },
  escalateUnitLabel: {
    id: `${scope}.escalateUnitLabel`,
    defaultMessage: 'Wait time unit',
    description:
      'Accessible label for whether the wait before the backup approvers are asked is counted in hours or days',
  },
  // Hours and Days are the two the design offers (§3) and the two the API's enum
  // publishes. Defined here rather than reusing `globalMessages.days`: the pair
  // is one vocabulary on one control, and splitting it across two catalogs would
  // let a reword of the global "Days" — which also serves "Days Left In Stage" —
  // silently change this picker.
  // Names the missing half rather than the pair: the number is on screen and
  // filled in, so "set a wait" would point at the field that is already right.
  escalateUnitRequired: {
    id: `${scope}.escalateUnitRequired`,
    defaultMessage: 'Choose whether the wait is counted in hours or days',
    description:
      'Validation message when a level carries a wait before its backup approvers are asked but does not say what the number counts',
  },
  escalateUnitHours: {
    id: `${scope}.escalateUnitHours`,
    defaultMessage: 'Hours',
    description: 'Option for counting the wait before the backup approvers are asked in hours',
  },
  escalateUnitDays: {
    id: `${scope}.escalateUnitDays`,
    defaultMessage: 'Days',
    description: 'Option for counting the wait before the backup approvers are asked in days',
  },
  // Only ever offered when a level already holds it. `Approvals::RuleLevel`
  // enumerizes it and the engine implements it, but the picker does not offer it
  // — Michaela's call on AXHE-3974 #5 — so this exists to keep a level that has
  // it legible and round-trippable rather than having it rewritten to Days on the
  // next save. See `escalateUnitOptions`.
  escalateUnitBusinessDays: {
    id: `${scope}.escalateUnitBusinessDays`,
    defaultMessage: 'Business days',
    description:
      'Option for counting the wait before the backup approvers are asked in working days, skipping weekends. Shown only on a level that already uses it',
  },
  // Action-phrased, per `copywriting-voice`'s error table — "Required field" ✗ /
  // "Enter a job title" ✓ — and it names the consequence rather than the field,
  // because a wait time with no number is not a blank input, it is a roster of
  // people the engine will never contact.
  escalateAfterRequired: {
    id: `${scope}.escalateAfterRequired`,
    defaultMessage: 'Enter how long to wait before the backup approvers are asked',
    description:
      'Validation message when a round of sign-off lists backup approvers but no wait time, so they would never be asked',
  },
  // The empty-field message above cannot serve this case. `escalateAfterValue`
  // also refuses a fraction and a figure past what the column stores, and on both
  // of those the field visibly holds a number — so "enter how long to wait" reads
  // as though the panel had not seen what was typed. Names what is wrong with the
  // figure instead, and still says what to do with it.
  escalateAfterInvalid: {
    id: `${scope}.escalateAfterInvalid`,
    defaultMessage: 'Enter the wait as a whole number of hours or days',
    description:
      'Validation message when the wait before the backup approvers are asked holds something the server would refuse, such as a fraction or a number too large to store',
  },
  // The mirror case, and a warning rather than a refusal: a wait time with nobody
  // on it comes from stored data, not from an edit here, and the level still works
  // on its primary approvers. Both exits are named — staff it, or remove the block.
  backupApproversMissing: {
    id: `${scope}.backupApproversMissing`,
    defaultMessage:
      'No backup approvers are set, so nobody takes over if this level gets no response. Add one, or remove the backup approvers.',
    description:
      'Warning on a round of sign-off that has a wait time but nobody to hand over to, naming both ways out',
  },
  // Why the remove control is withheld. Same posture as
  // `scopeConditionsElsewhere`: a panel that cannot show a row must not offer to
  // delete it, and removing the block would drop these with nothing on screen
  // having named them.
  //
  // "User attribute" is the design's own recruiter-facing noun for an approver
  // resolved from a role — §3: "Each approver row has a type: Name (a specific
  // person) or User attribute (a role)" — so it is the word the row type selector
  // will carry when AXHE-3899 lands, and saying "role" here would put two nouns
  // on one card for one thing. It does collide with the Attribute *condition*
  // type further down the same card, which is the half of the terminology
  // AXHE-3899 records as still open; that is the ticket to settle it on, not a
  // reason to mint a third word.
  backupApproversElsewhere: {
    id: `${scope}.backupApproversElsewhere`,
    defaultMessage:
      '{count, plural, one {# backup approver on this level is set by user attribute} other {# backup approvers on this level are set by user attribute}} and not shown here. They are kept as they are.',
    description:
      'Read-only notice on a level whose backup roster includes approvers resolved from a role — shown to the recruiter as "user attribute" — that the panel cannot render. Says that saving keeps them, which is why the block cannot be removed here',
  },
  addLevel: {
    id: `${scope}.addLevel`,
    defaultMessage: 'Add level',
    description: 'Button that appends another round of sign-off to the rule',
  },
  removeLevel: {
    id: `${scope}.removeLevel`,
    defaultMessage: 'Remove level',
    description: 'Button that deletes one round of sign-off from the rule',
  },
  reorderLevel: {
    id: `${scope}.reorderLevel`,
    defaultMessage: 'Reorder level {number}',
    description:
      'Accessible label for the handle that moves one round of sign-off earlier or later in the sequence. Says "reorder" rather than "drag" because the handle is operated by keyboard as well',
  },
  // Two labels, not one: creating a rule and saving an edit are different
  // actions, and the footer primary is the last thing read before either one
  // commits. `globalMessages.saveChanges` is not reused — it is "Save Changes",
  // and buttons are sentence case.
  //
  // The English matches `createRule` above, and the id deliberately does not:
  // that one opens a menu and this one writes the rule, which a translation may
  // well distinguish even where English does not.
  createRuleSubmit: {
    id: `${scope}.createRuleSubmit`,
    defaultMessage: 'Create approval rule',
    description: 'Button that writes a new approval rule',
  },
  saveRuleChanges: {
    id: `${scope}.saveRuleChanges`,
    defaultMessage: 'Save changes',
    description: 'Button that writes the edits made to an existing rule',
  },
  saveFailed: {
    id: `${scope}.saveFailed`,
    defaultMessage: "We couldn't save this rule. Try again.",
    description:
      'Shown when writing the rule failed, with the recovery action',
  },

  // --- row actions ---
  // "Enabled", matching the column header above it and the "Off" badge that
  // reports the same state. It read "Active" until review on #4599 pointed out
  // that the new column header made one row say three words for one concept —
  // header "Enabled", switch "Active", badge "Off" — where `copywriting-voice`
  // asks for one vocabulary across an experience. On/off is the switch's own
  // pairing, so the badge stays as it is.
  //
  // Reworded under the same id, per the recruiter-ui i18n rule. Note the
  // consequence: the 77 translated catalogs still hold their rendering of
  // "Active" until they are re-translated, which is the right trade here because
  // the two words name the same state.
  ruleActiveToggle: {
    id: `${scope}.ruleActiveToggle`,
    defaultMessage: 'Enabled',
    description:
      'Switch on a rule row controlling whether it currently gates anything. Matches the column header over it',
  },
  deleteRule: {
    id: `${scope}.deleteRule`,
    defaultMessage: 'Delete rule',
    description: 'Button that permanently removes an approval rule',
  },
  // Names the action, not the rule; the rule is named in the body sentence
  // instead. This is the design's, and nothing in the shell forces it — an
  // earlier revision of this dialog justified it by FDS `ModalHeader`'s
  // `noWrap`, which would have truncated a long account-authored rule name in
  // the heading, but that shell is gone and the heading here wraps. Recorded
  // because the reason is worth having on the record accurately: a short,
  // constant title reads as the action and leaves the rule name somewhere it
  // can wrap, which is a preference rather than a constraint (AXHE-4553).
  deleteRuleTitle: {
    id: `${scope}.deleteRuleTitle`,
    defaultMessage: 'Delete rule',
    description:
      'Heading of the confirmation asking whether to permanently remove an approval rule',
  },
  // Opens on the consequence, not on "Are you sure you want to" — the standard
  // in `.claude/skills/ux-design/copywriting-voice` names that opener as the
  // anti-pattern, and nothing here forces it: the name sits in the same
  // wrappable position either way. The prototype's own wording carries the
  // filler, and where the two disagree the skill wins.
  deleteRuleCascade: {
    id: `${scope}.deleteRuleCascade`,
    defaultMessage:
      'This permanently removes <b>{name}</b> and {count, plural, one {its # level} other {all # levels}} within it.',
    description:
      'Body of the delete confirmation. Names the rule being deleted and what else is destroyed alongside it, so the scale of the deletion is clear before confirming. <b> marks the rule name, which is the one part an admin has to check before confirming',
  },
  // Two consequences, in the order an admin can act on them: the requests that
  // stop, and what then happens to the subjects behind them. The second sentence
  // is the one the design added — an approval that is cancelled does not hold
  // its subject back, so it goes out unapproved unless another rule catches it.
  //
  // `subject` selects the noun because one rule table backs both filters: a rule
  // is either offers or openings, never both, and the design requirements spell
  // the consequence "those offers/openings" precisely because the dialog serves
  // whichever one is open. Printing both nouns on a surface the admin has
  // already filtered would be vaguer than the rule itself is.
  deleteRulePending: {
    id: `${scope}.deleteRulePending`,
    defaultMessage:
      'Deleting this rule will <b>cancel {count, plural, one {# pending approval request} other {# pending approval requests}}</b> currently using it. If no other rule applies, {subject, select, Funnel {those openings} other {those offers}} will pass through as <b>not needing approval</b>.',
    description:
      'Warning that the offers or openings currently awaiting sign-off under this rule stop being gated by it, and go out unapproved unless another rule applies. {subject} is the rule\'s subject type, which decides whether the sentence names offers or openings. <b> marks the count and the outcome, the two parts that change what the admin decides',
  },
  // Its own line rather than the tail of the body sentence: the design puts the
  // permanence note under the warning so the warning is what the eye lands on,
  // and a sentence about levels is not where an admin looks for "this is final".
  //
  // Worded as the standard spells it rather than the prototype's "This action
  // cannot be undone." — same sentence, house contraction (AXHE-4553).
  deleteRuleUndone: {
    id: `${scope}.deleteRuleUndone`,
    defaultMessage: "This can't be undone.",
    description:
      'Footnote under the delete confirmation, stating that the deletion is permanent',
  },
  toggleFailed: {
    id: `${scope}.toggleFailed`,
    defaultMessage: "We couldn't change this rule. Try again.",
    description:
      'Shown when switching a rule active or inactive failed, with the recovery action',
  },
  deleteFailed: {
    id: `${scope}.deleteFailed`,
    defaultMessage: "We couldn't delete this rule. Try again.",
    description: 'Shown when removing a rule failed, with the recovery action',
  },
  // --- condition builder (AXHE-3929) ---
  conditionsLabel: {
    id: `${scope}.conditionsLabel`,
    defaultMessage: "Conditions",
    description:
      "Heading for the rules deciding whether this level of sign-off applies at all",
  },
  addCondition: {
    id: `${scope}.addCondition`,
    defaultMessage: "Add condition",
    description: "Button that adds another condition to this level",
  },
  // Numbered by the row's place in the set, not by its draft key. The key is a
  // React identity (`scope-new-1`, `condition-2`) and reading it out is the
  // system language `constraint-anti-pattern-guard` prohibits — the same reason
  // `reorderLevel` names a level by its number.
  removeCondition: {
    id: `${scope}.removeCondition`,
    defaultMessage: 'Remove condition {number}',
    description:
      "Button that deletes one condition, numbered by its place in the set so each row's control has a distinct name",
  },
  conditionFieldLabel: {
    id: `${scope}.conditionFieldLabel`,
    defaultMessage: "Field",
    description: "Label for the data the condition is written about",
  },
  conditionOperatorLabel: {
    id: `${scope}.conditionOperatorLabel`,
    defaultMessage: "Comparison",
    description: "Label for how the field is compared, such as greater than",
  },
  conditionValueLabel: {
    id: `${scope}.conditionValueLabel`,
    defaultMessage: "Value",
    description: "Label for the literal the field is compared against",
  },
  conditionCompareKeyLabel: {
    id: `${scope}.conditionCompareKeyLabel`,
    defaultMessage: "Compare with field",
    description:
      "Label for the second field this condition compares against instead of a fixed value",
  },
  compareToFieldToggle: {
    id: `${scope}.compareToFieldToggle`,
    defaultMessage: "Compare with another field",
    description:
      "Switches the condition from comparing against a typed-in value to comparing against a second field",
  },
  conditionNeedsField: {
    id: `${scope}.conditionNeedsField`,
    defaultMessage: "Choose a field",
    description: "Validation message when a condition has no field selected",
  },
  conditionNeedsOperator: {
    id: `${scope}.conditionNeedsOperator`,
    defaultMessage: "Choose a comparison",
    description: "Validation message when a condition has no comparison selected",
  },
  conditionNeedsValue: {
    id: `${scope}.conditionNeedsValue`,
    defaultMessage: "Enter a value to compare against",
    description: "Validation message when a condition has nothing to compare the field with",
  },
  conditionNeedsCompareKey: {
    id: `${scope}.conditionNeedsCompareKey`,
    defaultMessage: "Choose a field to compare with",
    description:
      "Validation message when a condition compares against another field but none was selected",
  },
  noComparableFields: {
    id: `${scope}.noComparableFields`,
    defaultMessage: "No other field has the same type as this one",
    description:
      "Explains why the compare-with-field list is empty: comparisons are only offered between fields holding the same kind of data",
  },
  conditionsLoading: {
    id: `${scope}.conditionsLoading`,
    defaultMessage: "Loading fields",
    description:
      "Shown while the list of fields a condition can be written about is still being fetched",
  },
  conditionsLoadError: {
    id: `${scope}.conditionsLoadError`,
    defaultMessage:
      "We couldn't load the fields conditions are written about. Conditions on this rule are unchanged.",
    description:
      "Shown when the field catalog failed to load: conditions cannot be edited, and the ones already on the rule are left as they are",
  },
  conditionFieldUnavailable: {
    id: `${scope}.conditionFieldUnavailable`,
    defaultMessage: "This field is no longer available. Choose another.",
    description:
      "Validation message when a saved condition names a field the account no longer has",
  },
  // One message for both stale states the server distinguishes — the compare
  // target is gone, or both fields still exist and a type changed under them.
  // The admin's move is the same either way, and `copywriting-voice` asks for the
  // action rather than the diagnosis. Worded to match
  // `conditionOperatorUnavailable`, which is the same shape one control along.
  conditionCompareKeyUnavailable: {
    id: `${scope}.conditionCompareKeyUnavailable`,
    defaultMessage:
      'This field can no longer be compared with the one above. Choose another.',
    description:
      "Validation message when a saved condition compares against a second field the account has removed, or whose type no longer matches the first field's",
  },
  conditionOperatorUnavailable: {
    id: `${scope}.conditionOperatorUnavailable`,
    defaultMessage: "This comparison no longer applies to this field. Choose another.",
    description:
      "Validation message when a saved condition uses a comparison the field's type does not allow, usually because the field's type changed",
  },

  // --- operator labels (AXHE-4473) ---
  // The server sends its own tokens (`greater_than`); these are what an admin
  // reads. Phrased to complete the sentence the row spells out left to right —
  // "Sign-on bonus / greater than / 10000" — so each one is a verb phrase in
  // lower case rather than a title-cased label.
  operatorEquals: {
    id: `${scope}.operatorEquals`,
    defaultMessage: 'equals',
    description:
      "Comparison choice: the field's value is exactly the value given",
  },
  operatorNotEquals: {
    id: `${scope}.operatorNotEquals`,
    defaultMessage: 'does not equal',
    description:
      "Comparison choice: the field's value is anything other than the value given",
  },
  operatorContains: {
    id: `${scope}.operatorContains`,
    defaultMessage: 'contains',
    description:
      "Comparison choice: the field's text includes the value given somewhere within it",
  },
  operatorGreaterThan: {
    id: `${scope}.operatorGreaterThan`,
    defaultMessage: 'greater than',
    description:
      "Comparison choice: the field's value is above the value given",
  },
  operatorLessThan: {
    id: `${scope}.operatorLessThan`,
    defaultMessage: 'less than',
    description:
      "Comparison choice: the field's value is below the value given",
  },
  operatorIsPresent: {
    id: `${scope}.operatorIsPresent`,
    defaultMessage: 'is present',
    description:
      'Comparison choice: the field has any value at all, whatever it is',
  },

  // --- condition types (AXHE-4383) ---
  // A row now starts with what kind of thing it is about, which is what makes the
  // other six types authorable at all.
  conditionTypeLabel: {
    id: `${scope}.conditionTypeLabel`,
    defaultMessage: 'Condition type',
    description: 'Label for the kind of thing a condition is written about, such as a position or the applicant\'s pay',
  },
  // One divergence from the design's own `condTypeOptions`, and one thing that
  // looks like a divergence and is not.
  //
  // **"Data key" → "Offer field".** `copywriting-voice`'s terminology table lists
  // "Data keys" as wording not to use. Its replacement there is "Custom
  // attributes", which cannot be borrowed here: Design Requirements §4 carries a
  // locked Decision that Attribute and Data key are *two different concepts*, and
  // that an Attribute condition is the one reading the customer's own custom
  // attributes. Calling the other type a custom attribute would name it after the
  // concept it is not. §4 describes what it does read — "the offer's own
  // structured fields (e.g. base salary, sign-on bonus)" — and it is offers-only,
  // so that is the name.
  //
  // **"Attribute" stays "Attribute"**, per that same Decision.
  //
  // Sentence case throughout, so "Applicant Pay" in the mock is "Applicant pay".
  conditionTypeCompensation: {
    id: `${scope}.conditionTypeCompensation`,
    defaultMessage: 'Applicant pay',
    description: "Condition type: compares what the offer pays against a figure or the opening's posted range",
  },
  conditionTypeDataKey: {
    id: `${scope}.conditionTypeDataKey`,
    defaultMessage: 'Offer field',
    description:
      "Condition type: one of the offer's own structured fields, such as base salary or sign-on bonus. Offers only — an opening has no applicant to read one from.",
  },
  conditionTypeAttribute: {
    id: `${scope}.conditionTypeAttribute`,
    defaultMessage: 'Attribute',
    description:
      'Condition type: a custom attribute defined on the opening, its location or its position',
  },
  conditionTypePosition: {
    id: `${scope}.conditionTypePosition`,
    defaultMessage: 'Position',
    description: 'Condition type: the position the opening is for',
  },
  conditionTypeOpening: {
    id: `${scope}.conditionTypeOpening`,
    defaultMessage: 'Opening',
    description: 'Condition type: named openings',
  },
  conditionTypeLocation: {
    id: `${scope}.conditionTypeLocation`,
    defaultMessage: 'Location',
    description: 'Condition type: the location the opening is at, or a group of locations',
  },

  // --- attribute conditions ---
  // "Attribute source", not "Scope": the rule already has a scope, and this names
  // which record the value is read off — "Hiring manager of the opening" is not
  // "Hiring manager of the location".
  conditionAttributeScopeLabel: {
    id: `${scope}.conditionAttributeScopeLabel`,
    defaultMessage: 'Attribute source',
    description:
      "Label for which record an attribute's value is read from — the opening, its location, or its position",
  },
  // Its own label rather than borrowing the data-key row's "Field": Design
  // Requirements §4 keeps the two concepts apart, and a row whose type says
  // "Attribute" should not then ask for a "Field".
  //
  // "Attribute name", not a bare "Attribute", and the reason is mechanical. MUI
  // v4 labels a select with `aria-labelledby="<id>-label <id>"` — its own node
  // included — so the *selected value* becomes part of the accessible name. A
  // control labelled "Attribute" therefore shares a labelling element with the
  // type select whenever that select's value is "Attribute", and the two become
  // indistinguishable to anything matching on label text.
  conditionAttributeLabel: {
    id: `${scope}.conditionAttributeLabel`,
    defaultMessage: 'Attribute name',
    description: 'Label for the attribute a condition is written about',
  },
  attributeScopeOpening: {
    id: `${scope}.attributeScopeOpening`,
    defaultMessage: 'Opening',
    description: "Reads the attribute from the opening the offer belongs to",
  },
  attributeScopeLocation: {
    id: `${scope}.attributeScopeLocation`,
    defaultMessage: 'Location',
    description: "Reads the attribute from the opening's location",
  },
  attributeScopePosition: {
    id: `${scope}.attributeScopePosition`,
    defaultMessage: 'Position',
    description: "Reads the attribute from the opening's position",
  },
  // Refused by the server in a rule's scope, where an unevaluatable condition
  // drops the whole rule silently. Both ways out are named, because an admin who
  // wants this attribute can still have it one level down.
  conditionAttributeUnsourced: {
    id: `${scope}.conditionAttributeUnsourced`,
    defaultMessage:
      "Fountain can't read this attribute when scoping a rule. Choose another, or gate a level on it instead.",
    description:
      "Validation message when a rule's scope names a standard attribute the evaluation context cannot supply a value for, which the server refuses",
  },
  // The level counterpart, and not an error: an unevaluatable level is held for a
  // human rather than skipped, so the rule over-gates instead of missing one.
  // Said out loud because nothing else on the card would show it.
  conditionNeedsAttribute: {
    id: `${scope}.conditionNeedsAttribute`,
    defaultMessage: 'Choose an attribute',
    description: 'Validation message when a condition has no attribute selected',
  },
  conditionAttributeUnavailable: {
    id: `${scope}.conditionAttributeUnavailable`,
    defaultMessage: 'This attribute is no longer available. Choose another.',
    description:
      'Validation message when a saved condition names an attribute the account no longer has',
  },
  // A typed value can only stop fitting after the fact: the widget constrains
  // what an admin can enter, so a mismatch here is a stored value the field's
  // type or option list has moved out from under. `ConditionsValidator` refuses it
  // on save, and this is the only place the admin can be told which row to fix
  // rather than meeting a 422 on a field they never touched.
  conditionValueUnavailable: {
    id: `${scope}.conditionValueUnavailable`,
    defaultMessage: 'This value is no longer one of the options. Choose another.',
    description:
      "Validation message when a saved condition's value is outside the field's current option list",
  },
  conditionValueWrongType: {
    id: `${scope}.conditionValueWrongType`,
    defaultMessage: 'This value no longer fits this field. Enter one that does.',
    description:
      "Validation message when a saved condition's value cannot be read as the field's type, usually because the type changed under it",
  },
  conditionAttributeHoldsLevel: {
    id: `${scope}.conditionAttributeHoldsLevel`,
    defaultMessage:
      "Fountain can't read this attribute yet, so this level will ask for approval on every {subject, select, Funnel {opening} other {offer}} until it can",
    description:
      "Notice on a level whose condition names a standard attribute nothing supplies a value for, so the level always applies. {subject} is the rule's subject type",
  },

  // --- pay conditions ---
  // No currency symbol on either amount, and none in the summary either: an
  // offer's pay carries no currency and no period (AXHE-4063), so a "$" would be
  // a unit this surface cannot know. The design's mock shows one.
  conditionAmountLabel: {
    id: `${scope}.conditionAmountLabel`,
    defaultMessage: 'Amount',
    description: 'Label for the single pay figure a condition compares against',
  },
  conditionMinAmountLabel: {
    id: `${scope}.conditionMinAmountLabel`,
    defaultMessage: 'Minimum',
    description: 'Label for the bottom of the pay range a condition compares against',
  },
  conditionMaxAmountLabel: {
    id: `${scope}.conditionMaxAmountLabel`,
    defaultMessage: 'Maximum',
    description: 'Label for the top of the pay range a condition compares against',
  },
  useJobPayRangeToggle: {
    id: `${scope}.useJobPayRangeToggle`,
    defaultMessage: "Use the opening's pay range",
    description:
      'Switches a pay condition from amounts entered here to the range configured on the opening itself',
  },
  // Why a single-use type option is greyed. Without it the disabled control is
  // the only signal, which `constraint-anti-pattern-guard` treats as a dead
  // control.
  conditionTypeSingleUse: {
    id: `${scope}.conditionTypeSingleUse`,
    defaultMessage:
      'Some condition types can be used only once, so one already on another row is unavailable',
    description:
      'Explains why a condition type is listed but cannot be chosen: another row in this set already uses it, and the server allows it only once',
  },
  // The counterpart to `conditionPayNotAllowed`, for a type that will never be
  // placeable here rather than one waiting on platform work. It names no type:
  // this lands on the row's own type select, which already says which one.
  conditionTypeNotAllowed: {
    id: `${scope}.conditionTypeNotAllowed`,
    defaultMessage: "This condition type can't be used on this rule. Remove it.",
    description:
      "Validation message on a stored condition whose type this rule's subject or scope does not allow, so the rule cannot be saved until the row is removed",
  },
  conditionPayNotAllowed: {
    id: `${scope}.conditionPayNotAllowed`,
    defaultMessage: 'This condition cannot be saved yet. Remove it, or wait until pay conditions are available.',
    description:
      'Validation message on a stored pay condition the server currently refuses, so the rule cannot be saved until it is removed',
  },

  // --- comparisons on a pay condition ---
  // A separate vocabulary from the operator labels above, and two of the three
  // tokens collide with them: a pay condition's `greater_than` compares against
  // the *ceiling* of a range, so "above" is accurate where "greater than" is not.
  // `triggerSummary` already draws the same distinction.
  comparisonAbove: {
    id: `${scope}.comparisonAbove`,
    defaultMessage: 'above',
    description: 'Pay comparison: the offer pays more than the amount or the top of the range',
  },
  comparisonBelow: {
    id: `${scope}.comparisonBelow`,
    defaultMessage: 'below',
    description: 'Pay comparison: the offer pays less than the amount or the bottom of the range',
  },
  comparisonOutside: {
    id: `${scope}.comparisonOutside`,
    defaultMessage: 'outside',
    description: 'Pay comparison: the offer pays either less than the minimum or more than the maximum',
  },

  // --- entity conditions ---
  // Plural, because each control selects a set: one row can name several
  // positions and the rule matches any of them.
  conditionPositionsLabel: {
    id: `${scope}.conditionPositionsLabel`,
    defaultMessage: 'Positions',
    description: 'Label for the positions a condition applies to',
  },
  conditionLocationsLabel: {
    id: `${scope}.conditionLocationsLabel`,
    defaultMessage: 'Locations',
    description: 'Label for the individual locations a condition applies to',
  },
  conditionLocationGroupsLabel: {
    id: `${scope}.conditionLocationGroupsLabel`,
    defaultMessage: 'Location groups',
    description:
      'Label for the location groups a condition applies to, beside the individual Locations picker',
  },
  // The accessible name of a group header in the Locations menu. Its row shows
  // the group, a count and a tri-state checkbox; react-select announces a focused
  // row from this string rather than from the row's own markup, so the count and
  // the state are said here or not at all.
  //
  // {count} is what the header currently stands for — the members that survived
  // the search, which is also the set its checkbox describes and the set clicking
  // it toggles.
  conditionGroupOption: {
    id: `${scope}.conditionGroupOption`,
    defaultMessage: '{label}, {selected} of {count} selected',
    description:
      'Accessible name for a group header row in the locations picker. {label} is the group name, {selected} how many of the listed members the condition holds, {count} how many are listed',
  },
  conditionOpeningsLabel: {
    id: `${scope}.conditionOpeningsLabel`,
    defaultMessage: 'Openings',
    description: 'Label for the openings a condition applies to',
  },
  conditionNeedsPositions: {
    id: `${scope}.conditionNeedsPositions`,
    defaultMessage: 'Choose at least one position',
    description: 'Validation message when a position condition names none',
  },
  // Names both pickers, because either one alone is a usable scope — the rule
  // `Approvals::ConditionsValidator#errors_for_location` enforces. The sentence
  // renders against the Locations picker alone: one fault, one message.
  conditionNeedsLocations: {
    id: `${scope}.conditionNeedsLocations`,
    defaultMessage: 'Choose at least one location or location group',
    description: 'Validation message when a location condition names neither a location nor a group',
  },
  conditionNeedsOpenings: {
    id: `${scope}.conditionNeedsOpenings`,
    defaultMessage: 'Choose at least one opening',
    description: 'Validation message when an opening condition names none',
  },
  conditionNeedsAmount: {
    id: `${scope}.conditionNeedsAmount`,
    defaultMessage: 'Enter an amount greater than zero',
    description: 'Validation message when a pay condition has no usable figure to compare against',
  },
  conditionNeedsRange: {
    id: `${scope}.conditionNeedsRange`,
    defaultMessage: 'Enter a minimum and a maximum, both greater than zero',
    description: 'Validation message when a pay condition comparing against a range is missing a bound',
  },
  conditionBadRange: {
    id: `${scope}.conditionBadRange`,
    defaultMessage: 'Enter a minimum lower than the maximum',
    description: 'Validation message when a pay range condition has its bounds the wrong way round',
  },
  // The set-level rule, reported on the later of the two rows so the condition the
  // admin wrote first is left alone.
  conditionDuplicateType: {
    id: `${scope}.conditionDuplicateType`,
    defaultMessage: 'Only one condition of this type is allowed. Change the type, or remove this row.',
    description:
      'Validation message when a condition set holds two conditions of a type that may appear only once, such as two pay conditions',
  },
  // Indicating partial data, which `constraint-anti-pattern-guard` requires. It
  // matters most on a picker that searches: a search over a short list has to say
  // the list is short, or a missing location reads as one the account does not
  // have. No action is offered, because narrowing the list is the server's cap
  // rather than anything the admin can lift here.
  conditionOptionsTruncated: {
    id: `${scope}.conditionOptionsTruncated`,
    defaultMessage: 'Only the first {count, number} are listed',
    description:
      'Shown under an entity picker whose option list the server cut short, so a short list is not read as the whole list',
  },

  // --- how conditions combine (AXHE-3930's UI half) ---
  // Sentence case rather than `globalMessages.and` / `.or`, which are "AND" and
  // "OR" — those are used as inline connectors in older surfaces, and a pill is
  // sentence case.
  conditionLogicAnd: {
    id: `${scope}.conditionLogicAnd`,
    defaultMessage: 'And',
    description:
      'Connector between two conditions, meaning both of them have to be true for the level to apply',
  },
  conditionLogicOr: {
    id: `${scope}.conditionLogicOr`,
    defaultMessage: 'Or',
    description:
      'Connector between two conditions, meaning either one of them being true is enough for the level to apply',
  },
  conditionLogicToggle: {
    id: `${scope}.conditionLogicToggle`,
    defaultMessage:
      'Conditions on this level combine with {current}. Switch to {other}',
    description:
      'Accessible name of the connector between two conditions, saying how they currently combine and what clicking it changes them to',
  },
});
